import {
  ApiChartPayload,
  ApiChartTemplate,
  ApiChartType,
  ApiChartYAxisKey,
  ApiDimension,
  ApiAttributionModel,
} from "@incendium/api";
import { analyticsService } from "Apis";
import {
  filterNonTrendDimensions,
  filterTrendDimensions,
  getUniqueDimensionValues,
  hasDayTrend,
  hasHourTrend,
  isDimension,
} from "../utils/utils";
import { groupBy } from "Helpers/arrays";
import { friendlyDate, getDatesBetween } from "Helpers/dates";
import { IChart, TChartData } from "Interfaces";
import moment from "moment";

export const getFilterableDimensions = async (projectId: number) => {
  const res = await analyticsService.analyticsServiceGetFilterableDimensions({
    projectId,
  });
  return res.result;
};

export const getFilterableDimensionValues = async (
  projectId: number,
  dimension: ApiDimension
) => {
  const res = await analyticsService.analyticsServiceGetDimensionNames({
    projectId,
    dimension,
  });
  return res.result;
};
export async function getPrimaryAnalytics(
  projectId: number,
  chart: IChart,
  from?: Date | string,
  to?: Date | string,
  lastNDays?: number,
  model?: ApiAttributionModel,
  comparisonFrom?: Date | string,
  comparisonTo?: Date | string
): Promise<{
  chartData: TChartData[];
  dimensions: string[];
  totals?: TChartData;
  comparisonChartData?: TChartData[];
  comparisonTotals?: TChartData;
}> {
  from = from ? new Date(from) : undefined;
  to = to ? new Date(to) : undefined;
  comparisonFrom = comparisonFrom ? new Date(comparisonFrom) : undefined;
  comparisonTo = comparisonTo ? new Date(comparisonTo) : undefined;
  const response = await fetchPrimaryMetrics(
    projectId,
    chart,
    from,
    to,
    lastNDays,
    model,
    comparisonFrom,
    comparisonTo
  );

  if (!response.rows || response.rows.length === 0) {
    return emptyResult(response.dimensions);
  }

  let chartData = transformResponseRows(response);

  if (isTrendChart(chart)) {
    chartData = fillTrendGaps(chart, response, chartData, from, to, lastNDays);
  } else if (isTrendTemplate(chart)) {
    // For trend template charts that are not graphs, filter dates but don't fill gaps
    chartData = filterTrendDatesOnly(chart, chartData, from, to, lastNDays);
  }

  const totals = chart.withTotals
    ? computeTotals(chart, response, chartData)
    : undefined;

  // Handle comparison data if present
  let comparisonChartData: TChartData[] | undefined;
  let comparisonTotals: TChartData | undefined;

  if (response.comparisonRows && response.comparisonRows.length > 0) {
    const comparisonResponse = {
      ...response,
      rows: response.comparisonRows,
    };

    comparisonChartData = transformResponseRows(comparisonResponse);

    if (isTrendChart(chart)) {
      comparisonChartData = fillTrendGaps(
        chart,
        comparisonResponse,
        comparisonChartData,
        comparisonFrom,
        comparisonTo
      );
    } else if (isTrendTemplate(chart)) {
      // For trend template charts that are not graphs, filter dates but don't fill gaps
      comparisonChartData = filterTrendDatesOnly(
        chart,
        comparisonChartData,
        comparisonFrom,
        comparisonTo
      );
    }

    if (chart.withTotals) {
      comparisonTotals = computeTotals(chart, comparisonResponse, comparisonChartData);
    }
  }

  return {
    dimensions: response.dimensions || [],
    chartData,
    totals,
    comparisonChartData,
    comparisonTotals,
  };
}

// Fetch metrics from the analytics service
async function fetchPrimaryMetrics(
  projectId: number,
  chart: IChart,
  from?: Date,
  to?: Date,
  lastNDays?: number,
  model?: ApiAttributionModel,
  comparisonFrom?: Date,
  comparisonTo?: Date
) {
  return await analyticsService.analyticsServiceGetPrimaryMetrics({
    projectId,
    chartId: 0,
    payload: {
      from,
      to,
      lastNDays,
      chart: sanitizeChart(chart),
      leadId: chart.leadId,
      attributionModel: model,
      comparisonFrom,
      comparisonTo,
    },
  });
}

// Sanitize the chart object
function sanitizeChart(chart: IChart): ApiChartPayload {
  return {
    ...chart,
    attributes: (chart.attributes || []).filter((a) => a.value),
    having: chart.having?.filter((h) => typeof h.value !== "undefined"),
    yAxisKeys: (chart.yAxisKeys as ApiChartYAxisKey[]).map((y) => ({
      ...y,
      fields: y.fields?.filter((f) => f),
    })),
  };
}

// Return an empty result
function emptyResult(dimensions: string[] | undefined) {
  return {
    chartData: [],
    dimensions: dimensions || [],
    totals: undefined,
  };
}

// Transform response rows into chart data
function transformResponseRows(res: any): TChartData[] {
  return (res.rows || []).map((row) => {
    const dimensions = mapToObject(row.dimensions, res.dimensions);
    const metrics = mapToMetrics(row.metrics, res.metrics);
    return {
      name: row.dimensions ? row.dimensions[0] : "",
      ...dimensions,
      ...metrics,
    };
  });
}

// Map dimensions array to an object
function mapToObject(values: any[], keys: string[] | undefined): any {
  const result: any = {};
  (values || []).forEach((value, index) => {
    if (keys && keys[index]) {
      result[keys[index]] = value;
    }
  });
  return result;
}

// Map metrics array to an object
function mapToMetrics(values: any[], keys: string[] | undefined): any {
  const result: any = {};
  (values || []).forEach((value, index) => {
    if (keys && keys[index]) {
      result[keys[index]] = value.type?.includes("Int")
        ? Number(value.value)
        : value.value;
    }
  });
  return result;
}

// Check if the chart is a trend chart (template TREND and type GRAPH)
function isTrendChart(chart: IChart): boolean {
  return (
    chart.template === ApiChartTemplate.TREND &&
    chart.type === ApiChartType.GRAPH
  );
}

// Check if the chart has trend template (regardless of type)
function isTrendTemplate(chart: IChart): boolean {
  return chart.template === ApiChartTemplate.TREND;
}

// Filter data to only include dates within the valid range
function filterDataByDateRange(
  chartData: TChartData[],
  startDate: moment.Moment,
  endDate: moment.Moment
): TChartData[] {
  return chartData.filter((item) => {
    if (!item.name) return true; // Keep items without dates

    // Parse the date string and convert to UTC at start of day to ignore timezone
    const itemDate = moment.utc(item.name);
    if (!itemDate.isValid()) return true; // Keep invalid dates (let other logic handle them)

    // Start of day comparison to ignore time component
    const itemDateStart = itemDate.clone().startOf("day");

    return itemDateStart.isSameOrAfter(startDate) && itemDateStart.isSameOrBefore(endDate);
  });
}

// Filter trend dates only (without filling gaps) - for trend template charts that are not graphs
function filterTrendDatesOnly(
  chart: IChart,
  chartData: TChartData[],
  from?: Date,
  to?: Date,
  lastNDays?: number
): TChartData[] {
  const { startDate, endDate } = determineDateRange(
    chartData,
    from,
    to,
    lastNDays,
    chart
  );

  // Filter out any dates outside the valid range
  return filterDataByDateRange(chartData, startDate, endDate);
}

// Fill gaps in trend data
function fillTrendGaps(
  chart: IChart,
  res: any,
  chartData: TChartData[],
  from?: Date,
  to?: Date,
  lastNDays?: number
): TChartData[] {
  const { startDate, endDate } = determineDateRange(
    chartData,
    from,
    to,
    lastNDays,
    chart
  );

  // Filter out any dates outside the valid range
  const filteredData = filterDataByDateRange(chartData, startDate, endDate);

  const granularity = getGranularity(chart);
  const intervals = getDatesBetween(startDate, endDate, granularity);

  const groupedData = groupBy(filteredData, "name");
  const dimensionValues = getUniqueDimensionValues(res.rows, 1);

  return generateFilledData(
    intervals,
    groupedData,
    dimensionValues,
    res,
    chart
  );
}

// Determine the date range for trend data
function determineDateRange(
  chartData: TChartData[],
  from?: Date,
  to?: Date,
  lastNDays?: number,
  chart?: IChart
) {
  let startDate = lastNDays
    ? moment().clone().subtract(lastNDays, "days").utc().startOf("day")
    : moment(from as Date).utc();

  let endDate = lastNDays
    ? moment().utc().subtract(1, "day").endOf("day")
    : moment(to as Date).utc();

  // For week/month trends, adjust the date range to include full periods
  // For day/hour trends, use the strict dates provided by the user
  const dimensions = chart?.dimension as ApiDimension[];
  const isStrictDateRange = hasDayTrend(dimensions) || hasHourTrend(dimensions);

  if (!isStrictDateRange) {
    const granularity = getGranularity(chart!);

    if (granularity === "weeks") {
      // Adjust to start of week containing the from date
      startDate = startDate.clone().startOf("week");
      // Adjust to end of week containing the to date
      endDate = endDate.clone().endOf("week");
    } else if (granularity === "months") {
      // Adjust to start of month containing the from date
      startDate = startDate.clone().startOf("month");
      // Adjust to end of month containing the to date
      endDate = endDate.clone().endOf("month");
    }
  }

  return { startDate, endDate };
}

// Get granularity for the trend chart
function getGranularity(chart: IChart): "hours" | "days" | "weeks" | "months" {
  const dimension = filterTrendDimensions(chart.dimension as ApiDimension[])[0];
  switch (dimension) {
    case ApiDimension.DIMENSION_SESSION_START_BY_HOUR:
      return "hours";
    case ApiDimension.DIMENSION_SESSION_START_BY_DAY:
      return "days";
    case ApiDimension.DIMENSION_SESSION_START_BY_WEEK:
      return "weeks";
    case ApiDimension.DIMENSION_SESSION_START_BY_MONTH:
      return "months";
    default:
      return "days";
  }
}

// Generate filled data for all intervals
function generateFilledData(
  intervals: moment.Moment[],
  groupedData: any,
  dimensionValues: any[],
  res: any,
  chart: IChart
): TChartData[] {
  const data: TChartData[] = [];
  const secondDimension = filterNonTrendDimensions(res.dimensions || [])[0];

  intervals.forEach((interval) => {
    const key = friendlyDate(interval);
    const dateRows = groupedData[key];

    if (!secondDimension) {
      if (dateRows) {
        data.push(dateRows[0]);
      } else {
        data.push(generateEmptyRow(key, res.metrics));
      }
      return;
    }

    dimensionValues.forEach((value) => {
      const row = dateRows?.find((g) => g[secondDimension] === value);
      data.push(
        row ||
        generateEmptyRowWithDimension(
          key,
          value,
          secondDimension,
          res.metrics
        )
      );
    });
  });

  return data;
}

// Generate an empty row for missing data
function generateEmptyRow(name: string, metrics: any[]): TChartData {
  const row: any = { name };
  metrics.forEach((metric) => {
    row[metric] = 0;
  });
  return row;
}

// Generate an empty row with dimensions
function generateEmptyRowWithDimension(
  name: string,
  dimensionValue: string,
  secondDimension: string,
  metrics: any[]
): TChartData {
  const row: any = { name, [secondDimension]: dimensionValue };
  metrics.forEach((metric) => {
    row[metric] = 0;
  });
  return row;
}

// Compute totals for the chart
function computeTotals(
  chart: IChart,
  res: any,
  chartData: TChartData[]
): TChartData {
  const aggregate: any = { id: "Total", name: "Total" };
  if (res.totalsRow?.metrics?.length) {
    Object.entries(chartData[0]).forEach(([key]) => {
      if (["id", "name"].includes(key)) {
        aggregate[key] = "Total";
      } else if (isDimension(key)) {
        aggregate[key] = "Total";
      } else {
        const totalMetric = res.totalsRow.metrics.shift();
        aggregate[key] = (totalMetric.type || "").includes("Int")
          ? Number(totalMetric.value)
          : totalMetric.value;
      }
    });
  }
  return aggregate;
}

export const getAiInsights = async (
  projectId: number,
  chart: IChart,
  from?: Date,
  to?: Date,
  lastNDays?: number,
  model?: ApiAttributionModel
) => {
  const res = await analyticsService.analyticsServiceAnalyzeChart({
    projectId,
    chartId: 0,
    payload: {
      chart: sanitizeChart(chart),
      from,
      to,
      lastNDays,
      attributionModel: model,
    },
  });
  return res.result;
};